import * as Sentry from '@sentry/react';

// DSN y ambiente se leen de la configuración del front (runtime-env.sh reemplaza los placeholders al iniciar el contenedor).
// Sin DSN (o con el placeholder sin reemplazar) el monitoreo queda apagado.
const PLACEHOLDER = /^__RT_/;

const read = (value?: string): string => {
  const v = (value ?? '').trim();
  return PLACEHOLDER.test(v) ? '' : v;
};

export interface ServiceErrorContext {
  url?: string;
  status?: number;
  kind?: string;
}

const SENSITIVE_KEYS = /token|authorization|password|secret/i;

// Borra del evento cualquier cabecera o dato que parezca un token.
export const scrubEvent = <T extends Sentry.Event>(event: T): T => {
  const headers = event.request?.headers as Record<string, string> | undefined;
  if (headers) {
    for (const key of Object.keys(headers)) {
      if (SENSITIVE_KEYS.test(key)) delete headers[key];
    }
  }
  if (event.request?.cookies) delete event.request.cookies;
  return event;
};

// Quita query string de las URLs para no enviar datos en parámetros.
const stripQuery = (url?: string): string | undefined => url?.split('?')[0];

// Los breadcrumbs de fetch/xhr/navegación llevan URLs completas: se les saca la query.
export const scrubBreadcrumb = (breadcrumb: Sentry.Breadcrumb): Sentry.Breadcrumb => {
  const data = breadcrumb.data as Record<string, unknown> | undefined;
  if (data) {
    for (const key of ['url', 'from', 'to']) {
      if (typeof data[key] === 'string') data[key] = stripQuery(data[key] as string);
    }
  }
  return breadcrumb;
};

export const initMonitoring = (): boolean => {
  const dsn = read(process.env.REACT_APP_SENTRY_DSN);
  if (!dsn) return false;

  Sentry.init({
    dsn,
    environment: read(process.env.REACT_APP_SENTRY_ENVIRONMENT) || 'dev',
    release: read(process.env.REACT_APP_SENTRY_RELEASE) || undefined,
    // Captura global de errores JS no manejados y promesas rechazadas (GlobalHandlers, por defecto).
    beforeBreadcrumb: (breadcrumb) => scrubBreadcrumb(breadcrumb),
    beforeSend: (event) => scrubEvent(event)
  });
  const pais = read(process.env.REACT_APP_SENTRY_PAIS);
  if (pais) Sentry.setTag('pais', pais);
  return true;
};

export const captureServiceError = (error: unknown, context: ServiceErrorContext = {}): void => {
  if (!Sentry.getCurrentHub().getClient()) return;
  Sentry.withScope((scope) => {
    scope.setTag('origen', 'service');
    if (context.kind) scope.setTag('tipo', context.kind);
    if (context.status) scope.setTag('http.status', String(context.status));
    scope.setContext('request', { url: stripQuery(context.url) });
    // Agrupa por tipo + endpoint (sin query) para no generar un issue por cada URL con ids.
    scope.setFingerprint([context.kind ?? 'ServiceError', String(context.status ?? ''), stripQuery(context.url) ?? '']);
    Sentry.captureException(error instanceof Error ? error : new Error(String(error)));
  });
};
